import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { PageHeader } from "../components/PageHeader";
import { Toolbar, ToolbarGroup } from "../components/Toolbar";
import {
  publishPacks,
  reviewPacks,
  unpublishPacks,
  type PublishBatchResult,
} from "../lib/publishApi";
import { fetchLanguages } from "../lib/languagesApi";
import { fetchDayDetail, updatePackContent } from "../lib/readingsApi";
import {
  runAwitatpapuriScrape,
  runUsccbScrape,
  type ScrapeResult,
} from "../lib/scrapeApi";
import {
  defaultSectionLabels,
  responseFieldLabelFromConfig,
  sectionKindLabel,
  type SectionLabels,
} from "../lib/sectionLabels";
import type {
  DayDetailResponse,
  ReadingPackDetailDto,
  ReadingSection,
} from "../lib/types";

function cloneSections(sections: ReadingSection[]): ReadingSection[] {
  return sections.map((section) => ({
    ...section,
    items: section.items?.map((item) => ({ ...item })),
  }));
}

function sectionsEqual(a: ReadingSection[], b: ReadingSection[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function rebuildBodyFromItems(items: NonNullable<ReadingSection["items"]>): string {
  return items
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item) => item.body)
    .join("\n\n");
}

export function ReadingDayPage() {
  const { dateKey = "" } = useParams();
  const [data, setData] = useState<DayDetailResponse | null>(null);
  const [labelsByLang, setLabelsByLang] = useState<Record<string, SectionLabels>>({});
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<PublishBatchResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyName, setBusyName] = useState<string | null>(null);
  const [fetchBusy, setFetchBusy] = useState<"en" | "tl" | "both" | null>(null);
  const [fetchMessage, setFetchMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!dateKey) return;
    setLoading(true);
    setError(null);
    try {
      const [result, langs] = await Promise.all([
        fetchDayDetail(dateKey),
        fetchLanguages().catch(() => null),
      ]);
      setData(result);
      if (langs) {
        const map: Record<string, SectionLabels> = {};
        for (const lang of langs.languages) map[lang.code] = lang.labels;
        setLabelsByLang(map);
      }
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : "Failed to load day");
    } finally {
      setLoading(false);
    }
  }, [dateKey]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runPackAction(
    pack: ReadingPackDetailDto,
    action: "review" | "publish" | "unpublish",
  ) {
    setBusyName(`${pack.recordName}:${action}`);
    setActionError(null);
    setActionResult(null);
    try {
      const result =
        action === "review"
          ? await reviewPacks([pack.recordName])
          : action === "publish"
            ? await publishPacks([pack.recordName])
            : await unpublishPacks([pack.recordName]);
      setActionResult(result);
      if (result.failed > 0) {
        const first = result.results.find((r) => !r.ok);
        setActionError(first?.error ?? result.errorSummary);
      }
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Action failed.");
    } finally {
      setBusyName(null);
    }
  }

  function summarizeScrape(result: ScrapeResult): string {
    const day = result.results[0];
    if (day?.skippedPublished) {
      return `${result.languageCode.toUpperCase()}: published pack left untouched.`;
    }
    if (!day?.ok) {
      return `${result.languageCode.toUpperCase()}: ${day?.error ?? result.errorSummary ?? "failed"}`;
    }
    return `${result.languageCode.toUpperCase()}: fetched ${day.sectionCount ?? "?"} sections` +
      (day.title ? ` · ${day.title}` : "");
  }

  async function fetchFromSource(which: "en" | "tl" | "both") {
    if (!dateKey) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
      setActionError(`Invalid date in URL: ${dateKey}`);
      return;
    }
    setFetchBusy(which);
    setFetchMessage(null);
    setActionError(null);
    const range = { dateKeyStart: dateKey, dateKeyEnd: dateKey };
    const notes: string[] = [];
    const errors: string[] = [];

    async function runOne(lang: "en" | "tl") {
      const pack = data?.packs.find((p) => p.languageCode === lang);
      if (pack?.status === "published") {
        notes.push(`${lang.toUpperCase()}: published — skipped (unpublish to refetch).`);
        return;
      }
      try {
        const result =
          lang === "en"
            ? await runUsccbScrape(range)
            : await runAwitatpapuriScrape(range);
        notes.push(summarizeScrape(result));
      } catch (err) {
        errors.push(
          `${lang.toUpperCase()}: ${err instanceof Error ? err.message : "Fetch failed."}`,
        );
      }
    }

    try {
      if (which === "en" || which === "both") await runOne("en");
      if (which === "tl" || which === "both") await runOne("tl");
      if (notes.length) setFetchMessage(notes.join(" "));
      if (errors.length) setActionError(errors.join(" · "));
      await load();
    } finally {
      setFetchBusy(null);
    }
  }

  const day = data?.day;
  const title =
    day?.liturgicalTitleEN ?? data?.packs[0]?.liturgicalTitle ?? dateKey ?? "Reading day";
  const hasEn = data?.packs.some((p) => p.languageCode === "en") ?? false;
  const hasTl = data?.packs.some((p) => p.languageCode === "tl") ?? false;
  const emptyReadings = !hasEn && !hasTl;

  return (
    <AppShell>
      <PageHeader
        eyebrow={
          <span className="crumb">
            <Link className="link-inline" to="/readings">
              Calendar
            </Link>
            <span aria-hidden="true">/</span>
            <span>{dateKey}</span>
          </span>
        }
        title={title}
        description={
          <>
            Edit draft or reviewed packs, then publish. Section titles come from{" "}
            <Link className="link-inline" to="/languages">
              Languages
            </Link>
            . Fetch pulls USCCB (EN) / Awit at Papuri (TL); published packs are skipped.
          </>
        }
        actions={
          <Toolbar label="Source fetch">
            <ToolbarGroup>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={!!fetchBusy || !!busyName}
                onClick={() => void fetchFromSource("both")}
              >
                {fetchBusy === "both"
                  ? "Fetching…"
                  : emptyReadings
                    ? "Fetch EN + TL"
                    : "Refetch EN + TL"}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={!!fetchBusy || !!busyName}
                onClick={() => void fetchFromSource("en")}
                title={hasEn ? "Refetch English" : "Fetch English"}
              >
                {fetchBusy === "en" ? "…" : "EN"}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={!!fetchBusy || !!busyName}
                onClick={() => void fetchFromSource("tl")}
                title={hasTl ? "Refetch Tagalog" : "Fetch Tagalog"}
              >
                {fetchBusy === "tl" ? "…" : "TL"}
              </button>
            </ToolbarGroup>
            <Link className="btn btn-quiet btn-sm" to="/scrape">
              Bulk scrape
            </Link>
          </Toolbar>
        }
      />

      {day ? (
        <div className="meta-strip day-meta" aria-label="Liturgical day">
          {day.season ? (
            <span className="meta-pill">
              Season <strong>{day.season}</strong>
            </span>
          ) : null}
          {day.rank ? (
            <span className="meta-pill">
              Rank <strong>{day.rank}</strong>
            </span>
          ) : null}
          {day.color ? (
            <span className="meta-pill">
              Color <strong>{day.color}</strong>
            </span>
          ) : null}
          {day.lectionaryNumber ? (
            <span className="meta-pill">
              Lectionary <strong>{day.lectionaryNumber}</strong>
            </span>
          ) : null}
          <span className="meta-pill">
            Day published <strong>{day.isPublished ? "Yes" : "No"}</strong>
          </span>
          {day.liturgicalTitleTL ? (
            <span className="meta-pill">
              TL <strong>{day.liturgicalTitleTL}</strong>
            </span>
          ) : null}
        </div>
      ) : !loading ? (
        <div className="meta-strip day-meta">
          <span className="meta-pill">No LiturgicalDay record yet — fetch to create one</span>
        </div>
      ) : null}

      {fetchMessage && (
        <div className="banner-info" role="status" style={{ marginBottom: 12 }}>
          {fetchMessage}
        </div>
      )}

      {error && (
        <div className="error-banner" role="alert" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      {actionError && (
        <div className="error-banner" role="alert" style={{ marginBottom: 12 }}>
          {actionError}
        </div>
      )}

      {actionResult && actionResult.succeeded > 0 && !actionError && (
        <div className="banner-ok" role="status" style={{ marginBottom: 12 }}>
          {actionResult.action} succeeded for {actionResult.succeeded} pack(s).
        </div>
      )}

      {data?.warnings?.map((warning) => (
        <div key={warning} className="error-banner" role="status" style={{ marginBottom: 12 }}>
          {warning}
        </div>
      ))}

      {loading && !data ? (
        <div className="panel panel-pad" role="status">
          Loading day…
        </div>
      ) : !data?.packs.length ? (
        <div className="panel empty-panel">
          <h2>No reading packs</h2>
          <p>
            Fetch English and Tagalog from the source sites, or run a bulk scrape for a date
            range.
          </p>
          <Toolbar>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!!fetchBusy}
              onClick={() => void fetchFromSource("both")}
            >
              {fetchBusy === "both" ? "Fetching…" : "Fetch EN + TL"}
            </button>
            <Link className="btn btn-quiet" to="/scrape">
              Bulk scrape
            </Link>
          </Toolbar>
        </div>
      ) : (
        <div className="stack stack-gap-lg">
          <div className="section-title" style={{ marginBottom: 0 }}>
            <h2>Reading packs</h2>
            <p>
              Edit while <code>draft</code> or <code>reviewed</code>. Published packs are
              read-only until unpublished.
            </p>
          </div>

          {data.packs.map((pack) => (
            <PackReviewCard
              key={pack.recordName}
              pack={pack}
              labels={labelsByLang[pack.languageCode] ?? defaultSectionLabels(pack.languageCode)}
              busyName={busyName}
              onBusy={setBusyName}
              onAction={(action) => void runPackAction(pack, action)}
              onSaved={(next) => {
                setData((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    packs: prev.packs.map((row) =>
                      row.recordName === next.recordName ? next : row,
                    ),
                  };
                });
              }}
            />
          ))}
        </div>
      )}
    </AppShell>
  );
}

function PackReviewCard({
  pack,
  labels,
  busyName,
  onBusy,
  onAction,
  onSaved,
}: {
  pack: ReadingPackDetailDto;
  labels: SectionLabels;
  busyName: string | null;
  onBusy: (name: string | null) => void;
  onAction: (action: "review" | "publish" | "unpublish") => void;
  onSaved: (pack: ReadingPackDetailDto) => void;
}) {
  const editable = pack.status === "draft" || pack.status === "reviewed";
  const [title, setTitle] = useState(pack.liturgicalTitle ?? "");
  const [sections, setSections] = useState(() => cloneSections(pack.sections));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);

  useEffect(() => {
    setTitle(pack.liturgicalTitle ?? "");
    setSections(cloneSections(pack.sections));
    setSaveError(null);
    setSaveOk(false);
  }, [pack]);

  const dirty = useMemo(() => {
    const titleChanged = title.trim() !== (pack.liturgicalTitle ?? "").trim();
    return titleChanged || !sectionsEqual(sections, pack.sections);
  }, [title, sections, pack]);

  const saving = busyName === `${pack.recordName}:save`;

  async function onSave() {
    onBusy(`${pack.recordName}:save`);
    setSaveError(null);
    setSaveOk(false);
    try {
      const normalized = sections.map((section) => ({
        ...section,
        heading: "",
      }));
      const result = await updatePackContent({
        recordName: pack.recordName,
        sections: normalized,
        liturgicalTitle: title,
      });
      onSaved(result.pack);
      setSaveOk(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      onBusy(null);
    }
  }

  function updateSection(index: number, patch: Partial<ReadingSection>) {
    setSections((prev) =>
      prev.map((section, i) => (i === index ? { ...section, ...patch } : section)),
    );
    setSaveOk(false);
  }

  function updateItem(sectionIndex: number, sortOrder: number, body: string) {
    setSections((prev) =>
      prev.map((section, i) => {
        if (i !== sectionIndex || !section.items) return section;
        const items = section.items.map((item) =>
          item.sortOrder === sortOrder ? { ...item, body } : item,
        );
        return {
          ...section,
          items,
          body: rebuildBodyFromItems(items),
        };
      }),
    );
    setSaveOk(false);
  }

  function addItem(sectionIndex: number) {
    setSections((prev) =>
      prev.map((section, i) => {
        if (i !== sectionIndex) return section;
        const existing = section.items ?? [];
        const nextOrder =
          existing.length === 0
            ? 0
            : Math.max(...existing.map((item) => item.sortOrder)) + 1;
        const items = [...existing, { body: "", sortOrder: nextOrder }];
        return {
          ...section,
          items,
          body: rebuildBodyFromItems(items),
        };
      }),
    );
    setSaveOk(false);
  }

  function removeItem(sectionIndex: number, sortOrder: number) {
    setSections((prev) =>
      prev.map((section, i) => {
        if (i !== sectionIndex || !section.items) return section;
        const items = section.items
          .filter((item) => item.sortOrder !== sortOrder)
          .map((item, idx) => ({ ...item, sortOrder: idx }));
        return {
          ...section,
          items: items.length > 0 ? items : undefined,
          body: items.length > 0 ? rebuildBodyFromItems(items) : "",
        };
      }),
    );
    setSaveOk(false);
  }

  return (
    <div className="pack-card stack">
      <div className="pack-card-head">
        <div>
          <strong>
            {pack.languageCode.toUpperCase()}
            {pack.liturgicalTitle ? ` · ${pack.liturgicalTitle}` : ""}
          </strong>
          <div className="muted" style={{ fontSize: 13 }}>
            {pack.source ?? "unknown source"}
            {pack.sourceAttribution ? ` · ${pack.sourceAttribution}` : ""}
          </div>
        </div>
        <span
          className={`chip ${
            pack.status === "published" ? "" : pack.status === "reviewed" ? "warm" : "neutral"
          }`}
        >
          {pack.status}
        </span>
      </div>

      <div className="pack-toolbar">
        {editable && (
          <>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!dirty || !!busyName}
              onClick={() => void onSave()}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <ToolbarGroup>
              {pack.status === "draft" && (
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  disabled={!!busyName || dirty}
                  onClick={() => onAction("review")}
                >
                  {busyName === `${pack.recordName}:review` ? "…" : "Mark reviewed"}
                </button>
              )}
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={!!busyName || dirty}
                onClick={() => onAction("publish")}
              >
                {busyName === `${pack.recordName}:publish` ? "…" : "Publish"}
              </button>
            </ToolbarGroup>
          </>
        )}
        {pack.status === "published" && (
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            disabled={!!busyName}
            onClick={() => onAction("unpublish")}
          >
            {busyName === `${pack.recordName}:unpublish` ? "…" : "Unpublish"}
          </button>
        )}
        {pack.sourceURL && (
          <a
            className="btn btn-quiet btn-sm"
            href={pack.sourceURL}
            target="_blank"
            rel="noreferrer"
          >
            Source
          </a>
        )}
      </div>

      {dirty && editable && (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Unsaved changes — save before review or publish.
        </p>
      )}
      {saveError && (
        <div className="error-banner" role="alert">
          {saveError}
        </div>
      )}
      {saveOk && !dirty && (
        <div className="banner-ok" role="status">
          Saved.
        </div>
      )}
      {pack.status === "published" && (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Published — unpublish to edit.
        </p>
      )}

      {editable ? (
        <label className="field">
          Liturgical title
          <input
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setSaveOk(false);
            }}
            disabled={!!busyName}
          />
        </label>
      ) : null}

      {sections.length === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          No sections in <code>sectionsJSON</code>.
        </p>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {sections
            .map((section, originalIndex) => ({ section, originalIndex }))
            .sort((a, b) => a.section.sortOrder - b.section.sortOrder)
            .map(({ section, originalIndex: index }) => {
              const kindLabel = sectionKindLabel(labels, section.kind);
              const responseLabel = responseFieldLabelFromConfig(labels, section.kind);
              const isPsalm = section.kind === "psalm";
              const items =
                section.items && section.items.length > 0
                  ? section.items.slice().sort((a, b) => a.sortOrder - b.sortOrder)
                  : null;
              const showVerseEditor = isPsalm;

              if (!editable) {
                return (
                  <div key={`${pack.recordName}-${index}`} className="section-preview">
                    <div className="section-preview-head">
                      <span className="chip neutral">{kindLabel}</span>
                      <strong>{section.citation || "Untitled"}</strong>
                    </div>
                    {section.response ? (
                      <p className="section-response">
                        <span className="section-response-label">{responseLabel}</span>
                        <span className="section-response-text">{section.response}</span>
                      </p>
                    ) : null}
                    {items && items.length > 0 ? (
                      <div className="section-items">
                        {items.map((item) => (
                          <div
                            key={`${pack.recordName}-${index}-${item.sortOrder}`}
                            className="section-item"
                          >
                            <pre className="section-body section-body-full">{item.body}</pre>
                            {section.response ? (
                              <p className="section-response section-response-reuse">
                                <span className="section-response-label">
                                  {responseLabel} (reuse)
                                </span>
                                <span className="section-response-text">{section.response}</span>
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <pre className="section-body section-body-full">
                        {section.body || "(empty)"}
                      </pre>
                    )}
                  </div>
                );
              }

              return (
                <div key={`${pack.recordName}-${index}`} className="section-preview section-edit">
                  <div className="section-preview-head">
                    <span className="chip neutral">{kindLabel}</span>
                    <span className="muted" style={{ fontSize: 12 }}>
                      <code>{section.kind}</code>
                    </span>
                  </div>

                  <label className="field">
                    Citation
                    <input
                      value={section.citation}
                      onChange={(e) => updateSection(index, { citation: e.target.value })}
                      disabled={!!busyName}
                    />
                  </label>

                  <label className="field">
                    {responseLabel}
                    <textarea
                      className="section-textarea"
                      rows={section.kind === "psalm" || section.kind === "acclamation" ? 3 : 2}
                      value={section.response ?? ""}
                      onChange={(e) =>
                        updateSection(index, {
                          response: e.target.value.trim() ? e.target.value : undefined,
                        })
                      }
                      disabled={!!busyName}
                      placeholder={
                        section.kind === "acclamation"
                          ? labels.acclamationResponse
                          : labels.psalmResponse
                      }
                    />
                  </label>

                  {showVerseEditor ? (
                    <div className="section-items">
                      {(items ?? []).map((item, itemIndex) => (
                        <div
                          key={`${pack.recordName}-${index}-${item.sortOrder}`}
                          className="section-item"
                        >
                          <div className="section-item-toolbar">
                            <span className="muted" style={{ fontSize: 13 }}>
                              Verse {itemIndex + 1}
                            </span>
                            <button
                              type="button"
                              className="btn btn-quiet btn-sm"
                              disabled={!!busyName || (items?.length ?? 0) <= 1}
                              onClick={() => removeItem(index, item.sortOrder)}
                            >
                              Remove
                            </button>
                          </div>
                          <textarea
                            className="section-textarea"
                            rows={4}
                            value={item.body}
                            onChange={(e) => updateItem(index, item.sortOrder, e.target.value)}
                            disabled={!!busyName}
                          />
                          {section.response ? (
                            <p className="section-response section-response-reuse">
                              <span className="section-response-label">
                                {responseLabel} (reuse)
                              </span>
                              <span className="section-response-text">{section.response}</span>
                            </p>
                          ) : null}
                        </div>
                      ))}
                      {!items?.length && section.body ? (
                        <label className="field">
                          Body (no verse items yet)
                          <textarea
                            className="section-textarea"
                            rows={6}
                            value={section.body}
                            onChange={(e) => updateSection(index, { body: e.target.value })}
                            disabled={!!busyName}
                          />
                        </label>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        disabled={!!busyName}
                        onClick={() => {
                          if (!items?.length && section.body.trim()) {
                            updateSection(index, {
                              items: [{ body: section.body, sortOrder: 0 }],
                            });
                          } else {
                            addItem(index);
                          }
                        }}
                      >
                        {!items?.length && section.body.trim()
                          ? "Convert body to verse item"
                          : "Add verse"}
                      </button>
                    </div>
                  ) : (
                    <label className="field">
                      Body
                      <textarea
                        className="section-textarea"
                        rows={6}
                        value={section.body}
                        onChange={(e) => updateSection(index, { body: e.target.value })}
                        disabled={!!busyName}
                      />
                    </label>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
